import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import BaseBreadcrumbs from "../../components/BaseBreadcrumbs";
import usePaginatedList from "../../hooks/usePaginatedList"; // ✅ standard hook
import PaginatedTable from "../../components/PaginatedTable"; // ✅ standard table

/**
 * ComputeType Manager (Cluster-scoped)
 * \- Follows the "PlanManager" standard pattern (our truth):
 *   usePaginatedList + PaginatedTable, filters with Apply/Clear, CRUD via Modals.
 */

// ---------------- UI primitives ----------------
const cx = (...classes) => classes.filter(Boolean).join(" ");

const Button = ({ as: Tag = "button", className = "", children, ...rest }) => (
  <Tag
    className={cx(
      "inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-sm font-medium",
      "border bg-slate-800/80 border-slate-700 text-slate-200 hover:bg-slate-700/80",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60",
      "disabled:opacity-50 disabled:cursor-not-allowed",
      className
    )}
    {...rest}
  >
    {children}
  </Tag>
);

const DangerButton = (props) => (
  <Button
    {...props}
    className={cx(
      "border-red-800 bg-red-900/35 text-red-100 hover:bg-red-900/55",
      props.className
    )}
  />
);

const PrimaryButton = (props) => (
  <Button
    {...props}
    className={cx("border-indigo-700 bg-indigo-600 text-white hover:bg-indigo-500", props.className)}
  />
);

const Field = ({ label, children, help }) => (
  <label className="grid gap-1">
    <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
    {children}
    {help ? <span className="text-[11px] text-slate-500">{help}</span> : null}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={cx(
      "h-9 px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200",
      "placeholder:text-slate-500",
      "focus:outline-none focus:ring-2 focus:ring-indigo-500/60",
      props.className
    )}
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className={cx(
      "px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200",
      "placeholder:text-slate-500 min-h-[88px]",
      "focus:outline-none focus:ring-2 focus:ring-indigo-500/60",
      props.className
    )}
  />
);

const Modal = ({ open, title, onClose, children, footer }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-[min(820px,94vw)] rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
        <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-slate-800">
          <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
          <button
            onClick={onClose}
            className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="p-4 md:p-6 space-y-4">{children}</div>
        {footer ? (
          <div className="px-4 md:px-6 py-3 border-t border-slate-800 flex items-center justify-end gap-2">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const prettyJSON = (value) => {
  try { return JSON.stringify(value, null, 2); } catch { return String(value ?? ""); }
};

// ---------------- Main Page ----------------
export default function ComputeTypeManager() {
  const clusterId = JSON.parse(
                    localStorage.getItem("selected_cluster")
                ).id;

  // Endpoints (cluster-scoped)
  const API_LIST_BASE = `/admin/compute-types/cluster/${clusterId}`; // GET list (and detail if needed)
  const API_ADMIN_BASE = `/admin/compute-types/cluster/${clusterId}`; // POST/PUT/DELETE by id

  // Filters (staged -> applied)
  const [draft, setDraft] = useState({ search: "" });
  const [filters, setFilters] = useState(draft);

  // Fetcher (standard)
  const fetcher = useCallback(async (params) => {
    // Back-end supports page/page_size (and optionally skip/limit). Keep it simple.
    const { data } = await axiosInstance.get(`${API_LIST_BASE}/`, { params });
    return data; // {items, meta} or Array
  }, [API_LIST_BASE]);

  const {
    rows,
    meta,
    loading,
    error,
    page,
    pageSize,
    onChange,
    getRequestParams,
    refetch,
    reloadCurrentPageSafe,
  } = usePaginatedList(fetcher, { initialPage: 1, initialPageSize: 25, deps: [filters] });

  // Columns (base + a few dynamic extras)
  const columns = useMemo(() => {
    const sample = rows?.[0] || {};
    const base = ["id", "code", "name"]; // known fields
    const extra = Object.keys(sample)
      .filter((k) => ![...base, "cluster_id", "created_at", "updated_at"].includes(k))
      .slice(0, 4);

    return [...base, ...extra].map((key) => ({
      key,
      header: key.replaceAll("_", " "),
      className: key === "name" ? "font-medium text-slate-100" : undefined,
    }));
  }, [rows]);

  // CRUD state
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ code: "", name: "", description: "" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ code: "", name: "", description: "" });
  const [confirmDel, setConfirmDel] = useState(null);

  // Handlers
  const applyFilters = () => {
    setFilters({ ...draft });
    onChange({ page: 1, page_size: pageSize }); // reset to first page
  };
  const clearFilters = () => {
    const cleared = { search: "" };
    setDraft(cleared);
    setFilters(cleared);
    onChange({ page: 1, page_size: pageSize });
  };

  const startEdit = (row) => {
    setEditing(row);
    setEditForm({
      code: row.code ?? "",
      name: row.name ?? "",
      description: row.description ?? "",
    });
  };

  const onCreate = async () => {
    if (!createForm.code?.trim() || !createForm.name?.trim()) {
      alert("Code and Name are required.");
      return;
    }
    try {
      await axiosInstance.post(`${API_ADMIN_BASE}/`, {
        code: createForm.code,
        name: createForm.name,
        description: createForm.description || undefined,
      });
      setCreating(false);
      setCreateForm({ code: "", name: "", description: "" });
      onChange({ page: 1, page_size: pageSize }); // see the new item
    } catch (e) {
      alert(String(e?.response?.data?.detail || "Failed to create compute type"));
    }
  };

  const onUpdate = async () => {
    if (!editing?.id && !editing?.compute_type_id) {
      alert("Missing ID for update.");
      return;
    }
    const id = editing.id ?? editing.compute_type_id;
    try {
      await axiosInstance.put(`${API_ADMIN_BASE}/${id}`, {
        code: editForm.code,
        name: editForm.name,
        description: editForm.description || undefined,
      });
      setEditing(null);
      await refetch();
    } catch (e) {
      alert(String(e?.response?.data?.detail || "Failed to update"));
    }
  };

  const onDelete = async () => {
    if (!confirmDel?.id && !confirmDel?.compute_type_id) return;
    const id = confirmDel.id ?? confirmDel.compute_type_id;
    try {
      await axiosInstance.delete(`${API_ADMIN_BASE}/${id}`);
      setConfirmDel(null);
      await reloadCurrentPageSafe();
    } catch (e) {
      alert(String(e?.response?.data?.detail || "Failed to delete"));
    }
  };

  // Ensure list refresh when cluster changes (options-less page)
  useEffect(() => {
    // trigger via deps in usePaginatedList by touching filters (no-op value keeps equality)
    setFilters((f) => ({ ...f }));
  }, [clusterId]);

  return (
    <div className="bg-gradient-to-br from-black via-gray-900 to-gray-800 text-gray-100 min-h-screen">
      <div className="max-w-full mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
        {/* Breadcrumbs */}
        <BaseBreadcrumbs />

        {/* Header + actions */}
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Compute Types</h1>
          <div className="flex flex-wrap gap-2 md:ml-auto">
            <Input
              value={draft.search}
              onChange={(e) => setDraft((s) => ({ ...s, search: e.target.value }))}
              placeholder="Search by code or name…"
              className="w-64"
              aria-label="Search"
            />
            <Button onClick={applyFilters} disabled={loading}>Apply</Button>
            <Button onClick={clearFilters}>Clear</Button>
            <PrimaryButton onClick={() => setCreating(true)}>New</PrimaryButton>
          </div>
        </div>

        {/* Error banner */}
        {error ? (
          <div className="rounded-xl border border-red-800/50 bg-red-900/35 text-red-100 px-4 py-3">{String(error)}</div>
        ) : null}

        {/* Table (server-side pagination handled inside) */}
        <PaginatedTable
          rows={rows}
          loading={loading}
          error={error}
          meta={meta}
          onChange={onChange}
          columns={columns}
          actions={(row) => (
            <div className="inline-flex items-center gap-2">
              <Button onClick={() => startEdit(row)} className="h-8 px-2">Edit</Button>
              <DangerButton onClick={() => setConfirmDel(row)} className="h-8 px-2">Delete</DangerButton>
            </div>
          )}
          pageSizeOptions={[25, 40, 60, 100]}
          emptyText="No compute types found."
        />

        {/* Create Modal */}
        <Modal
          open={creating}
          title="Create Compute Type"
          onClose={() => setCreating(false)}
          footer={
            <>
              <Button onClick={() => setCreating(false)}>Cancel</Button>
              <PrimaryButton onClick={onCreate} disabled={loading}>Create</PrimaryButton>
            </>
          }
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Code" help="Unique within the cluster">
              <Input
                value={createForm.code}
                onChange={(e) => setCreateForm((s) => ({ ...s, code: e.target.value }))}
                placeholder="e.g. general, cpu-optimized"
              />
            </Field>
            <Field label="Name">
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="Human-friendly name"
              />
            </Field>
            <Field label="Description (optional)" className="md:col-span-2">
              <Textarea
                value={createForm.description}
                onChange={(e) => setCreateForm((s) => ({ ...s, description: e.target.value }))}
                placeholder="Short description"
              />
            </Field>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal
          open={!!editing}
          title={`Edit: ${editing?.name ?? editing?.code ?? "Item"}`}
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button onClick={() => setEditing(null)}>Cancel</Button>
              <PrimaryButton onClick={onUpdate} disabled={loading}>Save</PrimaryButton>
            </>
          }
        >
          <div className="grid gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Code">
                <Input value={editForm.code} onChange={(e) => setEditForm((s) => ({ ...s, code: e.target.value }))} />
              </Field>
              <Field label="Name">
                <Input value={editForm.name} onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))} />
              </Field>
            </div>
            <Field label="Description">
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm((s) => ({ ...s, description: e.target.value }))}
              />
            </Field>

            {editing ? (
              <Field label="Other fields (read-only)">
                <pre className="text-xs p-3 rounded-lg bg-slate-800 border border-slate-700 overflow-auto max-h-56">
                  {prettyJSON(
                    Object.fromEntries(
                      Object.entries(editing || {}).filter(([k]) => !["code", "name", "description"].includes(k))
                    )
                  )}
                </pre>
              </Field>
            ) : null}
          </div>
        </Modal>

        {/* Delete confirm */}
        <Modal
          open={!!confirmDel}
          title="Delete compute type"
          onClose={() => setConfirmDel(null)}
          footer={
            <>
              <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
              <DangerButton onClick={onDelete} disabled={loading}>Delete</DangerButton>
            </>
          }
        >
          <p className="text-slate-300">
            Are you sure you want to delete {" "}
            <span className="font-semibold">{confirmDel?.name ?? confirmDel?.code ?? "this item"}</span>?
          </p>
        </Modal>
      </div>
    </div>
  );
}